import { useCallback, useState } from 'react';
import { Card } from 'components/Card/Card';
import { getText } from 'i18n/intl';
import { get } from 'lodash';
import { fieldsSelector } from 'slices/field.slice';
import {
  hubStudentReservationSettingOnChange,
  hubStudentReservationSettingOnClear,
  hubStudentSettingsErrorsSelector,
  hubStudentSettingsSelector,
  hubStudentSettingsValidate,
} from 'slices/hubStudentSettings.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { TReservationTemplate, TType } from '@timeedit/types';
import { Alert, Select, TEInput, Typography } from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';
import { HubStudentReservationConfiguration } from '../../../../../types/hubStudentSettings.type';
import { createSortedFieldOrTypeOptions } from '../../../../../utils/objectTypes';
import { CommonValueInput, FieldsList } from '../../../components';
import { settingTypes } from '../../../constants';
import 'components/AppService/components/ReservationDisplaySettings.scss';

const LOCATION_SINGLE_TYPE_KEYS = [
  'room',
  'building',
  'campus',
  'type',
  'map',
  'image',
  'equipment',
] as const;

const EditHubStudentReservationSettings = () => {
  const [selectedRows, setSelectedRows] = useState({
    extraFields: [],
    locationExtraFields: [],
  });
  const dispatch = useAppDispatch();
  const allFields = useAppSelector(fieldsSelector);
  const allObjectTypes: TType[] = useAppSelector(selectObjectTypesList);
  const config: HubStudentReservationConfiguration = useAppSelector(
    hubStudentSettingsSelector
  )?.studentReservationDisplaySettings;
  const validationErrors = useAppSelector(hubStudentSettingsErrorsSelector);

  const typeOptions = createSortedFieldOrTypeOptions(allObjectTypes);
  const [roomType, setRoomType] = useState<string>(
    findSavedRoomType(config?.location)
  );

  const onChange = useCallback(
    (path: string) => (value: any) => {
      if (value == null || value === undefined) {
        dispatch(hubStudentReservationSettingOnClear({ path }));
      } else {
        dispatch(hubStudentReservationSettingOnChange({ path, value }));
      }
      if (validationErrors != null) {
        dispatch(hubStudentSettingsValidate(false));
      }
    },
    [dispatch, validationErrors]
  );

  const onArrayFieldChange = useCallback(
    (dataPath: string) =>
      (index: number | undefined, key: string, value: any) => {
        let path: string;
        if (index === undefined) {
          path = `${dataPath}.${key}`;
        } else {
          path = `${dataPath}[${index}].${key}`;
        }
        dispatch(
          hubStudentReservationSettingOnChange({
            path,
            value,
          })
        );
        if (validationErrors != null) {
          dispatch(hubStudentSettingsValidate(false));
        }
      },
    [dispatch, validationErrors]
  );

  const onRowSelect = (section: keyof typeof selectedRows) => (rows: any) => {
    if (!rows.length && !selectedRows[section].length) return;
    setSelectedRows((prev) => ({ ...prev, [section]: rows }));
  };

  const onRowDelete =
    (section: keyof typeof selectedRows, dataPath: string) => () => {
      if (!selectedRows[section]?.length) {
        return;
      }

      const newSelectedRow = get(config, dataPath, []).filter(
        (row) => !selectedRows[section].includes(row)
      );

      onChange(dataPath)(newSelectedRow.length === 0 ? [] : newSelectedRow);
    };

  const fieldHasError =
    (path: string) =>
    (key: string, index: number, type: string): boolean => {
      if (
        type === 'object-field' &&
        !get(config, path)?.[index]?.[key]?.includes('-')
      ) {
        return true;
      }
      return (
        validationErrors?.[
          `studentReservationDisplaySettings,${path.replace(
            '.',
            ','
          )},${index},${key}`
        ] != null
      );
    };

  const settingCardProps = {
    validationErrors,
    onChangeCreator: onChange,
  };

  const changeLockType = (val: any) => {
    LOCATION_SINGLE_TYPE_KEYS.forEach((key) => {
      if (config.location?.[key]?.type === 'object-field') {
        settingCardProps.onChangeCreator(`location.${key}.value`)(null);
      }
    });
    config.location?.extraFields?.forEach((extra, index) => {
      if (extra && extra.type === 'object-field') {
        settingCardProps.onChangeCreator(
          `location.extraFields[${index}].value`
        )(null);
      }
    });
    if (val == null) {
      setRoomType('');
      return;
    }
    setRoomType(`${val}`);
  };

  return (
    <div>
      <Typography.Title level={4}>Presentation</Typography.Title>
      <div className="reservation-display-settings-main-container">
        <Card
          title="Module"
          className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
        >
          <div className="container">
            <SettingCard
              {...settingCardProps}
              title={getText('name')}
              type={config?.module?.name}
              path="module.name"
            />
            <SettingCard
              {...settingCardProps}
              title="Code"
              type={config?.module?.code}
              path="module.code"
            />
          </div>
        </Card>

        <Card
          title="Activity"
          className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
        >
          <div className="container">
            <SettingCard
              {...settingCardProps}
              title={getText('name')}
              type={config?.activity?.displayName}
              path="activity.displayName"
            />
            <SettingCard
              {...settingCardProps}
              title={getText('alternative_title')}
              type={config?.activity?.alternativeTitle}
              path="activity.alternativeTitle"
            />
          </div>
          <Alert
            className="reservation-display-settings-help-text"
            description={
              <Typography.Text>{getText('hub_activity_info')}</Typography.Text>
            }
            type="info"
            showIcon={false}
          />
        </Card>

        <Card
          title="Location"
          className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
        >
          <div className="reservation-display-settings-type-dropdown">
            <TEInput.Select
              label={`${getText('objects_type')} for Location`}
              tooltip={`Changing this will clear all selected fields for Location.`}
              size="small"
              onChange={changeLockType}
              value={roomType}
              options={typeOptions}
              allowClear={false}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div className="container">
            <SettingCard
              {...settingCardProps}
              title={getText('name')}
              type={config?.location?.room}
              lockedType
              lockedTypeId={roomType}
              path="location.room"
            />
            <SettingCard
              {...settingCardProps}
              title="Building"
              type={config?.location?.building}
              lockedType
              lockedTypeId={roomType}
              path="location.building"
            />
            <SettingCard
              {...settingCardProps}
              title="Campus"
              type={config?.location?.campus}
              lockedType
              lockedTypeId={roomType}
              path="location.campus"
            />
            <SettingCard
              {...settingCardProps}
              title={getText('type')}
              type={config?.location?.type}
              lockedType
              lockedTypeId={roomType}
              path="location.type"
            />
            <SettingCard
              {...settingCardProps}
              title="Map URL"
              type={config?.location?.map}
              lockedType
              lockedTypeId={roomType}
              path="location.map"
            />
            <SettingCard
              {...settingCardProps}
              title="Picture URL"
              type={config?.location?.image}
              lockedType
              lockedTypeId={roomType}
              path="location.image"
            />
            <SettingCard
              {...settingCardProps}
              title="Equipment"
              lockedType
              lockedTypeId={roomType}
              type={config?.location?.equipment}
              path="location.equipment"
            />
            <Card
              title="Extra fields"
              size="small"
              styles={{ body: { padding: 0 } }}
              className="reservation-display-settings-fields-list-card reservation-display-settings-flex-grow"
            >
              <FieldsList
                lockedType
                lockedTypeId={roomType}
                disableWidth
                columnSet="labelAndWidth"
                types={settingTypes}
                onChange={onChange('location.extraFields')}
                onFieldChange={onArrayFieldChange('location.extraFields')}
                onAddRow={() => {
                  onChange('location.extraFields')([
                    ...(config?.location?.extraFields ?? []),
                    { id: uuidv4() },
                  ]);
                }}
                values={config?.location?.extraFields}
                allTypes={allObjectTypes}
                allFields={allFields}
                extraOptions={[]}
                onRowSelect={onRowSelect('locationExtraFields')}
                onRowDelete={onRowDelete(
                  'locationExtraFields',
                  'location.extraFields'
                )}
                hasError={fieldHasError('location.extraFields')}
              />
            </Card>
          </div>
        </Card>

        <Card
          title="Digital resources"
          className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
        >
          <div className="container">
            <SettingCard
              {...settingCardProps}
              title="Online meeting link"
              type={config?.location?.onlineMeetingLink}
              path="location.onlineMeetingLink"
            />
            <SettingCard
              {...settingCardProps}
              title="Recording"
              type={config?.digitalResources?.recording}
              path="digitalResources.recording"
            />
            <SettingCard
              {...settingCardProps}
              title="Learning management system link"
              type={config?.digitalResources?.lmsLink}
              path="digitalResources.lmsLink"
            />
          </div>
        </Card>

        <Card
          title="Teaching staff"
          className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
        >
          <div className="container">
            <SettingCard
              {...settingCardProps}
              title={getText('name')}
              type={config?.staff?.name}
              path="staff.name"
            />
          </div>
        </Card>

        <Card
          title="Message to student"
          className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
        >
          <div className="container">
            <SettingCard
              {...settingCardProps}
              title={getText('message')}
              type={config?.messageToStudent}
              path="messageToStudent"
            />
          </div>
        </Card>
        <Card
          title="Extra fields"
          className="reservation-display-settings-flex-grow reservation-display-settings-location-card"
        >
          <div className="container">
            <FieldsList
              disableWidth
              columnSet="labelAndWidth"
              types={settingTypes}
              onChange={onChange('extraFields')}
              onFieldChange={onArrayFieldChange('extraFields')}
              onAddRow={() => {
                onChange('extraFields')([
                  ...(config?.extraFields ?? []),
                  { id: uuidv4() },
                ]);
              }}
              values={config?.extraFields}
              allTypes={allObjectTypes}
              allFields={allFields}
              extraOptions={[]}
              onRowSelect={onRowSelect('extraFields')}
              onRowDelete={onRowDelete('extraFields', 'extraFields')}
              hasError={fieldHasError('extraFields')}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

function findTypeId(value: string | undefined) {
  return value?.split('-')[0];
}

function findSavedRoomType(
  location: HubStudentReservationConfiguration['location']
): string {
  const values = [
    location?.room?.value,
    location?.building?.value,
    location?.campus?.value,
    location?.type?.value,
    location?.equipment?.value,
  ];
  for (const value of values) {
    const typeId = findTypeId(value);
    if (typeId) return typeId;
  }
  return '';
}

const SettingCard = (props: {
  title: string;
  reservationTemplate?: TReservationTemplate;
  objectTypeIds?: (string | number)[];
  fieldLabel?: string;
  objectLabel?: string;
  type: any;
  lockedType?: boolean;
  lockedTypeId?: string;
  path: string;
  validationErrors: Record<string, z.ZodIssue> | null;
  onChangeCreator: (path: string) => (newValue: any) => void;
  required?: boolean;
}) => {
  const allFields = useAppSelector(fieldsSelector);
  const allObjectTypes: TType[] = useAppSelector(selectObjectTypesList);

  const errorPath = `studentReservationDisplaySettings,${props.path.replace(
    '.',
    ','
  )}`;

  const hasError = useCallback(
    (path: string) => {
      return props.validationErrors?.[path] != null;
    },
    [props.validationErrors]
  );

  const status = useCallback(
    (path: string): 'error' | undefined => {
      return hasError(path) ? 'error' : undefined;
    },
    [hasError]
  );

  return (
    <Card
      title={
        <>
          {props.title}{' '}
          {props.required && (
            <span className="reservation-display-settings-required">*</span>
          )}
        </>
      }
      size="small"
      className="reservation-display-settings-flex-grow"
    >
      <Space
        direction="vertical"
        className="reservation-display-settings-full-width"
      >
        <TEInput.Input
          label="Display name"
          value={props.type?.displayName}
          onChange={(val) =>
            props.onChangeCreator(`${props.path}.displayName`)(
              val === '' ? undefined : val
            )
          }
          status={status(`${errorPath},displayName`)}
        />

        <Select
          value={props.type?.type}
          placeholder={getText('select')}
          allowClear={true}
          options={settingTypes}
          onChange={(val) => {
            props.onChangeCreator(`${props.path}.type`)(val);
            props.onChangeCreator(`${props.path}.value`)(null);
          }}
          status={
            hasError(`${errorPath},type`) && props.type?.type == null
              ? 'error'
              : undefined
          }
          popupMatchSelectWidth={false}
        />
        <CommonValueInput
          allFields={allFields}
          allTypes={allObjectTypes}
          reservationTemplate={props.reservationTemplate}
          type={{ ...props.type, label: '' }}
          lockedType={props.lockedType}
          lockedTypeId={props.lockedTypeId}
          onChange={(val) => {
            if (!props.type?.type && props.lockedType) {
              props.onChangeCreator(`${props.path}.type`)(
                val ? settingTypes[0].value : null
              );
            }
            props.onChangeCreator(`${props.path}.value`)(val);
          }}
          fieldLabel={props.fieldLabel ?? getText('field')}
          objectLabel={props.objectLabel ?? getText('objects_type')}
          valueLabel={getText('text')}
          objectTypeIds={props.objectTypeIds}
          hasError={hasError(`${errorPath},value`)}
        />
      </Space>
    </Card>
  );
};

export default EditHubStudentReservationSettings;
